import React, { useEffect, useRef, useState } from 'react';
import { Phone, PhoneOff, X } from 'lucide-react';
import { Spinner } from 'reactstrap';
import Api from 'services/api';
import { useDispatch, useSelector } from 'react-redux';
import { userSelector } from 'slices/userSlice';
import echo from 'services/echo';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { setInterpreterIframeUrl, setInterpreterAppid } from 'slices/interpreter/interpreterIframeSlice';

// ─── Single call card ────────────────────────────────────────────────────────
const CallCard = ({ call, onAccept, onReject, loadingId }) => {
  const isLoading = loadingId === call.id;

  return (
    <div style={{
      background: '#1a1a1a',
      borderRadius: 16,
      padding: '16px 18px',
      color: 'white',
      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
      animation: 'vri_slide_in 0.25s ease-out',
      width: 300,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 12, color: '#999', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Incoming Video Call
          </div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{call.language}</div>
          {call.appid && (
            <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>Appointment #{call.appid}</div>
          )}
        </div>
        <button
          onClick={() => onReject(call.id)}
          disabled={isLoading}
          style={{ background: 'none', border: 'none', color: '#666', cursor: 'pointer', padding: 4, lineHeight: 1 }}
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
      </div>

      <div style={{ display: 'flex', gap: 10 }}>
        <button
          onClick={() => onReject(call.id)}
          disabled={isLoading}
          style={{
            flex: 1, padding: '10px 0', background: '#dc2626', border: 'none',
            borderRadius: 10, color: 'white', fontWeight: 600, fontSize: 14,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          <PhoneOff size={15} /> Reject
        </button>
        <button
          onClick={() => onAccept(call.id)}
          disabled={isLoading}
          style={{
            flex: 1, padding: '10px 0', background: '#16a34a', border: 'none',
            borderRadius: 10, color: 'white', fontWeight: 600, fontSize: 14,
            cursor: isLoading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
            opacity: isLoading ? 0.6 : 1,
          }}
        >
          {isLoading ? <Spinner size="sm" /> : <><Phone size={15} /> Accept</>}
        </button>
      </div>
    </div>
  );
};

// ─── API helper ──────────────────────────────────────────────────────────────
async function acceptVriCall(id) {
  try {
    const { data, status } = await Api.acceptVriCall(id);
    if (status === 200) return { success: true, data };
    return { success: false, message: data?.message };
  } catch (error) {
    return { success: false, message: error?.response?.data?.message };
  }
}

// ─── Container ───────────────────────────────────────────────────────────────
const IncomingCalls = () => {
  const [calls, setCalls] = useState([]);   // newest first
  const [loadingId, setLoadingId] = useState(null);
  const { user } = useSelector(userSelector);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const ringtoneRef = useRef(null);

  const isOnVideoCallPage = location.pathname.includes('join-room-call') || location.pathname.includes('quick-join');

  // When interpreter navigates into the video call page, clear the queue and kill audio
  useEffect(() => {
    if (isOnVideoCallPage) {
      setCalls([]);
    }
  }, [isOnVideoCallPage]);

  // Single shared audio: play when first call arrives, stop only when queue empties
  useEffect(() => {
    if (calls.length > 0 && !isOnVideoCallPage) {
      if (!ringtoneRef.current) {
        const audio = new Audio('/ringtone.mp3');
        audio.loop = true;
        ringtoneRef.current = audio;
        audio.play().catch(() => {});
      }
    } else {
      const audio = ringtoneRef.current;
      if (audio) {
        audio.pause();
        audio.src = '';
        audio.load();
        ringtoneRef.current = null;
      }
    }
  }, [calls.length, isOnVideoCallPage]);

  // Destroy audio on component unmount
  useEffect(() => {
    return () => {
      const audio = ringtoneRef.current;
      if (audio) {
        audio.pause();
        audio.src = '';
        audio.load();
        ringtoneRef.current = null;
      }
    };
  }, []);

  // Late-login recovery: check for any active call on mount
  useEffect(() => {
    if (!user?.role?.includes('interpreter')) return;
    Api.getActiveVriCall().then((res) => {
      if (res?.status === 200 && res?.data?.data) {
        const call = res.data.data;
        setCalls((prev) =>
          prev.some((c) => c.id === call.id) ? prev : [call, ...prev]
        );
      }
    });
  }, [user]);

  // Pusher subscription
  useEffect(() => {
    if (!user?.role?.includes('interpreter')) return;

    const channel = echo.channel(`interpreter.${user?.user?.id}`);
    channel
      .listen('.incoming-call', (data) => {
        if (!data?.callData?.id) return;
        // Deduplicate: ignore if this call is already in the stack
        setCalls((prev) =>
          prev.some((c) => c.id === data.callData.id) ? prev : [data.callData, ...prev]
        );
      })
      .listen('.call-taken', (data) => {
        setCalls((prev) => prev.filter((c) => c.id !== data?.appointmentId));
      })
      .listen('.invite-expired', (data) => {
        setCalls((prev) => prev.filter((c) => c.id !== data?.appointmentId));
      })
      .error((err) => console.log('Pusher channel error:', err));

    return () => {
      channel.stopListening('.incoming-call');
      channel.stopListening('.call-taken');
      channel.stopListening('.invite-expired');
    };
  }, [user]);

  const removeCall = (id) => setCalls((prev) => prev.filter((c) => c.id !== id));

  const handleAccept = async (callId) => {
    setLoadingId(callId);
    const { data, success, message } = await acceptVriCall(callId);
    setLoadingId(null);
    if (success) {
      setCalls([]);   // clear entire queue — interpreter is now in a call
      dispatch(setInterpreterIframeUrl(data?.iframe_url));
      dispatch(setInterpreterAppid(data?.appointment?.appid));
      navigate(
        `/interpreter/appointments/view/${data.appointment?.id}/join-room-call`,
        { state: { isReplacement: data?.is_replacement === true } }
      );
    } else {
      removeCall(callId);
      toast.error(message || 'Something went wrong', { className: 'errorToast' });
    }
  };

  const handleReject = (callId) => {
    removeCall(callId);
    Api.dismissVriCall(callId);
  };

  // All hooks must run before this conditional return
  if (isOnVideoCallPage || calls.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes vri_slide_in {
          from { opacity: 0; transform: translateX(40px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
      <div style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
      }}>
        {calls.map((call) => (
          <CallCard
            key={call.id}
            call={call}
            onAccept={handleAccept}
            onReject={handleReject}
            loadingId={loadingId}
          />
        ))}
      </div>
    </>
  );
};

export default IncomingCalls;
